// The platform page, at `/console`: for a Provey super admin, every firm on
// the platform and the other super admins. Nobody else needs it — everybody's
// own two-step sign-in, and a firm's owners' and admins' people, are in the
// portal's Settings (`AccountSettings` in `teamAdmin.js`).
//
// **Its own surface, like the agent and reseller portals**, and for the same
// reason: it imports nothing from `App.js`. It shares `shared.js` and the
// sign-in pieces in `staffAuth.js`.
//
// **What it offers comes from `/accounts/me`.** `capabilities` and
// `superAdmin` are the middleware's answer, computed from the same map its
// routes ask, so this never draws a button the route refuses — and never
// decides anybody's access itself. See docs/decisions/staff-sign-in.md.

import { useState, useEffect, useCallback } from "react";
import { T, LOGO_SRC, MIDDLEWARE_URL } from "./shared";
import { SignInScreen, accountsFetch, readStoredSession, signOut, Btn, Note, Label, inputStyle } from "./staffAuth";
import {
  Section, Badge, Table, useAction, Feedback, PeoplePanel, FirmSettings,
  STATUS_LABELS, formatDate, td,
} from "./teamAdmin";

// ── The platform: every firm ───────────────────────────────────────────────

function FirmsPanel({ me }) {
  const [firms, setFirms] = useState(null);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", ownerName: "", ownerEmail: "", requireTwoFactor: true });
  const action = useAction();

  const load = useCallback(() => {
    accountsFetch("/platform/firms").then(d => setFirms(d.firms)).catch(e => action.setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    const out = await action.run(
      () => accountsFetch("/platform/firms", { method: "POST", body: form }),
      (o) => (o.invitationSent === false
        ? `Created ${o.firm.name}. The owner's invitation could not be sent — use Resend once mail is working.`
        : `Created ${o.firm.name} and invited ${o.owner.email} as its owner.`),
    );
    if (out) {
      setForm({ name: "", ownerName: "", ownerEmail: "", requireTwoFactor: true });
      setCreating(false);
      load();
    }
  };

  const firm = open && firms?.find(f => f.firmId === open);
  if (firm) {
    const id = encodeURIComponent(firm.firmId);
    return (
      <>
        <div style={{ marginBottom: 16 }}><Btn variant="quiet" onClick={() => { setOpen(null); load(); }}>← All firms</Btn></div>
        <h1 style={{ fontWeight: 300, fontSize: 26, letterSpacing: "-0.02em", color: T.text, margin: "0 0 20px" }}>
          {firm.name} {firm.status === "suspended" && <Badge tone="red">Suspended</Badge>}
        </h1>
        <FirmSettings firm={firm} canEdit allowStatus patchPath={`/platform/firms/${id}`}
          onSaved={(saved) => setFirms(list => list.map(f => (f.firmId === saved.firmId ? { ...f, ...saved } : f)))} />
        <PeoplePanel me={me} firmName={firm.name}
          paths={{ list: `/platform/firms/${id}/users`, user: "/platform/users" }}
          can={{ invite: true, role: true, remove: true, reset: true, anyRole: true }} />
      </>
    );
  }

  return (
    <Section title="Firms" action={!creating && <Btn onClick={() => setCreating(true)}>New firm</Btn>}>
      <Feedback action={action} />
      {creating && (
        <form onSubmit={create} style={{ background: T.bg, borderRadius: 10, padding: 16, marginBottom: 16, display: "grid", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
            <div><Label htmlFor="f-name">Firm name</Label><input id="f-name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} /></div>
            <div><Label htmlFor="f-owner-name">Owner's name</Label><input id="f-owner-name" value={form.ownerName} onChange={e => setForm({ ...form, ownerName: e.target.value })} style={inputStyle} /></div>
            <div><Label htmlFor="f-owner-email">Owner's email</Label><input id="f-owner-email" type="email" required value={form.ownerEmail} onChange={e => setForm({ ...form, ownerEmail: e.target.value })} style={inputStyle} /></div>
          </div>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: T.text }}>
            <input type="checkbox" checked={form.requireTwoFactor} onChange={e => setForm({ ...form, requireTwoFactor: e.target.checked })} />
            Require two-step sign-in for everybody in the firm
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn type="submit" disabled={action.busy}>{action.busy ? "Creating…" : "Create firm and invite owner"}</Btn>
            <Btn variant="quiet" onClick={() => setCreating(false)}>Cancel</Btn>
          </div>
        </form>
      )}
      {!firms ? <div style={{ fontSize: 13, color: T.textFaint }}>Loading…</div> : (
        <Table head={["Firm", "Status", "Two-step", "People", "Created", ""]}>
          {firms.map(f => (
            <tr key={f.firmId}>
              <td style={td}><span style={{ fontWeight: 500 }}>{f.name || "Unnamed"}</span>{f.isDefault && <span style={{ color: T.textFaint }}> · the platform's own firm</span>}</td>
              <td style={td}><Badge tone={f.status === "suspended" ? "red" : "ink"}>{STATUS_LABELS[f.status] || f.status}</Badge></td>
              <td style={td}>{f.requireTwoFactor ? "Required" : "Optional"}</td>
              <td style={td}>{f.userCount}</td>
              <td style={td}>{formatDate(f.createdAt)}</td>
              <td style={{ ...td, textAlign: "right" }}><Btn variant="secondary" onClick={() => setOpen(f.firmId)}>Open</Btn></td>
            </tr>
          ))}
        </Table>
      )}
    </Section>
  );
}

function AdminsPanel({ me }) {
  const [admins, setAdmins] = useState(null);
  const [form, setForm] = useState({ email: "", name: "" });
  const action = useAction();
  const load = useCallback(() => {
    accountsFetch("/platform/admins").then(d => setAdmins(d.admins)).catch(e => action.setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const grant = async (e) => {
    e.preventDefault();
    const out = await action.run(() => accountsFetch("/platform/admins", { method: "POST", body: form }),
      (o) => (o.invitationSent ? `Invited ${form.email} as a super admin.` : `${form.email} is now a super admin.`));
    if (out) { setForm({ email: "", name: "" }); load(); }
  };
  const revoke = (u) => {
    if (!window.confirm(`Take super admin away from ${u.email}?`)) return;
    action.run(() => accountsFetch(`/platform/admins/${encodeURIComponent(u.userId)}`, { method: "DELETE" }), `${u.email} is no longer a super admin.`).then(load);
  };

  return (
    <Section title="Super admins">
      <p style={{ fontSize: 13, color: T.textSoft, margin: "0 0 16px", lineHeight: 1.6 }}>
        Super admins create firms and manage anybody's account. They always use two-step sign-in.
      </p>
      <Feedback action={action} />
      <form onSubmit={grant} style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 16 }}>
        <div style={{ flex: 2, minWidth: 200 }}><Label htmlFor="a-email">Email</Label><input id="a-email" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle} /></div>
        <div style={{ flex: 1, minWidth: 160 }}><Label htmlFor="a-name">Name</Label><input id="a-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} /></div>
        <Btn type="submit" disabled={action.busy}>Add super admin</Btn>
      </form>
      {admins && (
        <Table head={["Person", "Two-step", "Last sign-in", ""]}>
          {admins.map(u => (
            <tr key={u.userId}>
              <td style={td}><div style={{ fontWeight: 500 }}>{u.name || u.email}</div>{u.name && <div style={{ fontSize: 12, color: T.textSoft }}>{u.email}</div>}</td>
              <td style={td}>{u.twoFactor ? "On" : <span style={{ color: T.warn }}>Not set up yet</span>}</td>
              <td style={td}>{formatDate(u.lastSignInAt)}</td>
              <td style={{ ...td, textAlign: "right" }}>
                {u.userId !== me.user.userId && <Btn variant="quiet" style={{ color: T.danger }} onClick={() => revoke(u)}>Remove</Btn>}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Section>
  );
}

// ── Firms' own contract templates, sent to be coded ────────────────────────
//
// A firm that wants its own contract template sends its precedent from
// Settings; the middleware emails it to the super admins and lists it here.
// Once coded, the template (.html) is uploaded against the firm, which
// switches the firm onto it. The middleware checks the template renders
// before it stores it.
async function platformFetch(path, { method = "GET", body } = {}) {
  const token = readStoredSession();
  const res = await fetch(`${MIDDLEWARE_URL}${path}`, {
    method,
    headers: { ...(body ? { "Content-Type": "application/json" } : {}), ...(token ? { "x-portal-token": token } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok || !data?.ok) {
    const err = new Error(data?.error || `The request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

const MAX_TEMPLATE_KB = 1000;

export function TemplatesPanel() {
  const [requests, setRequests] = useState(null);
  const action = useAction();

  const load = useCallback(() => {
    platformFetch("/platform/contract-template-requests").then(d => setRequests(d.requests))
      .catch(e => { setRequests([]); action.setError(e.status === 404 ? "Contract template requests need the latest middleware." : e.message); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = (row, file) => {
    if (!file) return;
    if (file.size > MAX_TEMPLATE_KB * 1024) { action.setError(`The template must be under ${MAX_TEMPLATE_KB}KB`); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const out = await action.run(
        () => platformFetch(`/platform/firms/${encodeURIComponent(row.firmId)}/contract-template`, {
          method: "POST", body: { html: String(reader.result || ""), name: file.name.replace(/\.html?$/i, "") },
        }),
        `Uploaded. ${row.firmName || "The firm"} now prints its own template.`,
      );
      if (out) load();
    };
    reader.readAsText(file);
  };

  return (
    <Section title="Contract templates" hint="Precedents firms have sent to be coded. Upload the coded template (.html) to switch the firm onto it.">
      <Feedback action={action} />
      {!requests ? <div style={{ fontSize: 13, color: T.textFaint }}>Loading…</div>
        : requests.length === 0 ? <div style={{ fontSize: 13, color: T.textFaint }}>No firm has sent a template.</div> : (
          <Table head={["Firm", "Precedent", "Sent", "Status", ""]}>
            {requests.map(r => (
              <tr key={r.firmId}>
                <td style={td}><span style={{ fontWeight: 500 }}>{r.firmName || r.firmId}</span>
                  {r.licenceNumber && <span style={{ display: "block", color: T.textFaint, fontSize: 12 }}>Licence {r.licenceNumber}</span>}</td>
                <td style={td}>{r.request.fileName}</td>
                <td style={td}>{formatDate(r.request.requestedAt)}{r.request.requestedBy && <span style={{ display: "block", color: T.textFaint, fontSize: 12 }}>{r.request.requestedBy}</span>}</td>
                <td style={td}><Badge tone={r.request.status === "done" ? "ink" : "warn"}>{r.request.status === "done" ? "Uploaded" : "To code"}</Badge></td>
                <td style={{ ...td, textAlign: "right" }}>
                  <input type="file" accept=".html,.htm,text/html" aria-label={`Upload the coded template for ${r.firmName || r.firmId}`} disabled={action.busy}
                    onChange={e => { upload(r, e.target.files?.[0]); e.target.value = ""; }} style={{ fontSize: 12 }} />
                </td>
              </tr>
            ))}
          </Table>
        )}
    </Section>
  );
}

// ── The console ────────────────────────────────────────────────────────────

export default function Console() {
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("");

  if (!me) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg }}>
        <SignInScreen returnPath="/console" intro="Sign in to manage the firms on the platform." onSignedIn={(account) => setMe(account)} />
      </div>
    );
  }

  // **Nobody but a super admin has anything here.** Everybody's own sign-in
  // and a firm's people are in the portal's Settings, so anybody else is
  // sent there rather than shown an empty page.
  if (!me.superAdmin) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 20px", fontFamily: "Inter, sans-serif" }}>
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: 32, maxWidth: 420 }}>
          <h1 style={{ fontWeight: 300, fontSize: 22, letterSpacing: "-0.02em", color: T.text, margin: "0 0 8px" }}>This page is for super admins</h1>
          <p style={{ fontSize: 13, color: T.textSoft, lineHeight: 1.6, margin: "0 0 20px" }}>
            Your two-step sign-in{me.capabilities.includes("users:invite") ? ", and the people at " + (me.firm.name || "your firm") + "," : ""} are in the portal's Settings.
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Btn onClick={() => window.location.assign("/")}>Open the portal</Btn>
            <Btn variant="secondary" onClick={() => signOut().then(() => setMe(null))}>Sign out</Btn>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [["firms", "Firms"], ["templates", "Contract templates"], ["admins", "Super admins"]];
  const active = tab || tabs[0][0];

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "Inter, sans-serif" }}>
      <header style={{ background: T.white, boxShadow: "0 1px 0 rgba(0,0,0,0.06)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px", minHeight: 56, display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
          <img src={LOGO_SRC} alt="Provey" style={{ height: 20, width: "auto", display: "block" }} />
          <nav aria-label="Console" style={{ display: "flex", gap: 22, flex: 1, flexWrap: "wrap", alignSelf: "stretch" }}>
            {tabs.map(([key, label]) => (
              <button key={key} type="button" aria-current={active === key ? "page" : undefined} onClick={() => setTab(key)}
                style={{ background: "none", border: "none", borderBottom: `2px solid ${active === key ? T.red : "transparent"}`, borderRadius: 0,
                  padding: 0, fontSize: 13.5, fontWeight: active === key ? 600 : 500, color: active === key ? T.text : T.textSoft,
                  cursor: "pointer", fontFamily: "Inter, sans-serif", display: "flex", alignItems: "center" }}>{label}</button>
            ))}
          </nav>
          <a href="/" style={{ fontSize: 13, color: T.textSoft, textDecoration: "none" }}>Open the portal</a>
          <span style={{ fontSize: 13, color: T.text }}>{me.user.name || me.user.email}</span>
          <Btn variant="secondary" onClick={() => signOut().then(() => setMe(null))}>Sign out</Btn>
        </div>
      </header>
      <main style={{ maxWidth: 1100, margin: "0 auto", padding: 24 }}>
        {me.firm.status === "suspended" && <div style={{ marginBottom: 16 }}><Note tone="warn">{me.firm.name} is suspended. Only super admins can sign in.</Note></div>}
        {active === "firms" && <FirmsPanel me={me} />}
        {active === "templates" && <TemplatesPanel />}
        {active === "admins" && <AdminsPanel me={me} />}
      </main>
    </div>
  );
}
